import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = logged out, object = user
  const [checked, setChecked] = useState(false);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    } finally {
      setChecked(true);
    }
  }, []);

  const loginWithGoogleSession = useCallback(async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    if (data.token) localStorage.setItem("token", data.token);
    await refreshUser();
    return data;
  }, [refreshUser]);

  useEffect(() => {
    // If returning from Emergent Google OAuth, the session_id arrives in the URL fragment.
    // Exchange it for our app session FIRST, then load the user (skip the premature /auth/me).
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      (async () => {
        try {
          if (sid) await loginWithGoogleSession(sid);
          else await refreshUser();
        } catch {
          await refreshUser();
        } finally {
          window.history.replaceState(null, "", window.location.pathname + window.location.search);
        }
      })();
      return;
    }
    refreshUser();
  }, [refreshUser, loginWithGoogleSession]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("token", data.token);
    await refreshUser();
    return data;
  }, [refreshUser]);

  const register = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/register", { email, password });
    if (data.token) localStorage.setItem("token", data.token);
    await refreshUser();
    return data;
  }, [refreshUser]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.error("Logout request failed", e); }
    localStorage.removeItem("token");
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, checked, login, register, logout, refreshUser, setUser, loginWithGoogleSession }),
    [user, checked, login, register, logout, refreshUser, loginWithGoogleSession]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { formatApiError };
